import { appPalette } from 'theme/App.palette';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';

const colors = appPalette.colors;

function focusableStyles(offset: number) {
  return {
    outline: `0.125rem solid`,
    outlineOffset: `${offset}rem`,
  };
}

export const CustomInput = withStyles(() =>
  createStyles({
    root: {
      fontFamily: 'inherit',
      fontSize: 'inherit',
      letterSpacing: 'inherit',
      lineHeight: 'inherit',
    },
    input: {
      position: 'relative',
      color: colors.black,
      width: '100%',
      boxSizing: 'border-box',
      border: `0.0625rem solid ${colors.mid_grey_2}`,
      margin: 0,
      outline: 'none',
      padding: '0.625rem 1.25rem',
      '& input': {
        height: '100%',
      },
      '&:focus': {
        'body:not(.acr-focus-disabled) &': {
          ...focusableStyles(0),
          outlineColor: colors.black,
        },
      },
    },
  })
)(InputBase);

export const CustomMenuItem = withStyles(() =>
  createStyles({
    root: {
      position: 'relative',
      fontFamily: 'inherit',
      fontSize: 'inherit',
      letterSpacing: 'inherit',
      lineHeight: 'inherit',
      padding: '0.625rem',
      wordWrap: 'break-word',
      whiteSpace: 'normal',
      minHeight: 'unset',
      '&.Mui-selected, &.Mui-selected:hover': {
        backgroundColor: 'white',
        outlineColor: colors.black,
      },
    },
  })
)(MenuItem);

export const useStyles = makeStyles(() => ({
  root: {
    display: 'block',
  },
  checkbox: {
    padding: '0',
    marginRight: '0.625rem',
  },
  filterDropdown: {
    '&:after': {
      top: '0.8125rem',
      right: 0,
      marginRight: '1.25rem',
      width: '0.8125rem',
      height: '0.8125rem',
      content: '""',
      display: 'block',
      zIndex: '2',
      position: 'absolute',
      transform: 'rotate(135deg)',
      borderTop: '0.1875rem solid #e82c2a',
      borderRight: '0.1875rem solid #e82c2a',
      pointerEvents: 'none',
    },
    '&.select-open': {
      boxShadow: '0.125rem 0.125rem 0.8125rem 0 rgb(0 0 0 / 16%)',
      '&:after': {
        transform: 'rotate(315deg)',
        top: '1.25rem',
      },
    },
    '&.Mui-focused': {
      '&>.MuiSelect-select:focus': {
        backgroundColor: 'transparent',
      },
    },
  },
  filterTooltip: {
    marginLeft: 'auto',
    borderRadius: '50%',
    border: `0.0625rem solid ${colors.light_grey_3}`,
    background: colors.white,
    color: colors.acura_red,
    height: '1.25rem',
    width: '1.25rem',
  },
  listItem: {
    '&:focus': {
      'body:not(.acr-focus-disabled) &': {
        ...focusableStyles(-0.2),
        outlineColor: colors.black,
      },
    },
  },
  filterCard: {
    position: 'absolute',
    zIndex: 1301,
    border: `0.125rem solid ${colors.light_grey_2}`,
    borderTop: `0.0625rem solid ${colors.light_grey_2}`,
    borderRadius: 0,
    padding: '2.1875rem 0.9375rem',
    width: '100%',
  },
  close_button: {
    display: 'flex',
    position: 'absolute',
    right: '0.9375rem',
    top: '0.9375rem',
    border: 0,
    padding: 0,
    backgroundColor: 'transparent',
    '& svg': {
      borderRadius: '5rem',
      width: '1.25rem',
      height: '2em',
      transition: 'all 0.4s ease-in-out',
      boxSizing: 'border-box',
    },
    '&:hover svg': {
      cursor: 'pointer',
      transform: 'rotate(90deg)',
    },
  },
  checkboxIcon: {
    backgroundColor: colors.off_white,
    bottom: 0,
    height: '1.25rem',
    left: 0,
    right: 'auto',
    width: '1.25rem',
    '$root.Mui-focusVisible &': {
      outline: '0.125rem auto rgba(19,124,189,.6)',
      outlineOffset: 2,
    },
    'input:hover ~ &': {
      backgroundColor: colors.off_white,
    },
    'input:disabled ~ &': {
      boxShadow: 'none',
      background: 'rgba(206,217,224,.5)',
    },
  },
  checkboxIconChecked: {
    backgroundColor: colors.off_white,
    backgroundImage:
      'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
    bottom: 0,
    height: '1.25rem',
    left: 0,
    right: 'auto',
    width: '1.25rem',
    '&:after': {
      top: '0.3125rem',
      left: '0.15625rem',
      width: '0.9375rem',
      height: '0.375rem',
      content: '""',
      display: 'block',
      zIndex: '2',
      position: 'absolute',
      transform: 'rotate(135deg)',
      borderTop: '0.0625rem solid #e82c2a',
      borderRight: '0.0625rem solid #e82c2a',
      pointerEvents: 'none',
    },
    'input:hover ~ &': {
      backgroundColor: colors.off_white,
    },
  },
  formControl: {
    position: 'relative',
    minWidth: 120,
    width: '100%',
    backgroundColor: 'white',
    marginBottom: '1.25rem',
  },
  hidden: {
    display: 'none',
  },
  selectMenu: {
    borderRadius: 0,
    boxShadow: '0 0.125rem 1.875rem 0 rgb(0 0 0 / 10%)',
    maxHeight: 320,
    borderBottom: `0.0625rem solid ${colors.mid_grey_2}`,
    borderLeft: `0.0625rem solid ${colors.mid_grey_2}`,
    borderRight: `0.0625rem solid ${colors.mid_grey_2}`,
    boxSizing: 'content-box',
    '&::-webkit-scrollbar': {
      width: '0.3em',
    },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 0.375rem rgba(0,0,0,0.00)',
      webkitBoxShadow: 'inset 0 0 0.375rem rgba(0,0,0,0.00)',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0,0,0,.1)',
      outline: '0.0625rem solid #d5d5d5',
    },
  },
  selectList: {
    padding: 0,
    width: 'inherit',
  },
  selectedItem: {
    backgroundColor: 'unset',
  },
}));
